import { useEffect, useState } from "react";
import {
  createRecommendPlaceApi,
  deleteRecommendPlaceApi,
  filterRecommendPlacesApi,
  updateRecommendPlaceApi,
} from "../../api/recommend-place";
import Datatable from "../../component/datatable";
import Pagination from "../../component/pagination";
import Meta from "../../interface/meta";
import {
  ICreateRecommendPlace,
  IRecommendPlace,
  IUpdateRecommendPlace,
} from "../../interface/recommend-place";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";
import AddRecommendPlaceModal from "./add-recommend-place-modal";
import EditRecommendPlaceModal from "./edit-recommend-place-modal";

const SettingRecommendPlace = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState({
    show: false,
    place: {},
  });
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState<IRecommendPlace[]>([]);
  const [meta, setMeta] = useState<Meta>({
    page: 0,
    limit: 10,
    totalCount: 0,
    hasMore: true,
  });
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<number[]>([0, 1]);

  const filterPlaces = async (
    page: number,
    limit: number,
    searchKeyword?: string,
    statusFilter?: number[]
  ) => {
    try {
      setLoading(true);
      const { data } = await filterRecommendPlacesApi({
        keyword: searchKeyword || keyword,
        status: statusFilter || [0, 1],
        page,
        limit,
      });

      setPlaces(data.results ?? []);
      setMeta({
        page: page,
        limit: limit,
        totalCount: data.meta.totalCount,
        hasMore: data.meta.hasMore,
      });
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something went wrong.");
    }
  };

  const addNewPlace = async (payload: ICreateRecommendPlace) => {
    try {
      setLoading(true);
      await createRecommendPlaceApi(payload);
      await filterPlaces(meta.page, meta.limit);
      setLoading(false);
      setShowAddModal(false);
      openSnackbar("สร้างสถานที่แนะนำสำเร็จ");
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something went wrong.");
    }
  };

  const updatePlace = async (id: string, payload: IUpdateRecommendPlace) => {
    try {
      setLoading(true);
      await updateRecommendPlaceApi(id, payload);
      await filterPlaces(meta.page, meta.limit);
      setLoading(false);
      setShowEditModal({ show: false, place: {} });
      openSnackbar("อัปเดตสถานที่แนะนำสำเร็จ");
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something went wrong.");
    }
  };

  const deletePlace = async (id: string) => {
    try {
      setLoading(true);
      await deleteRecommendPlaceApi(id);
      await filterPlaces(meta.page, meta.limit);
      setLoading(false);
      openSnackbar("ลบสถานที่แนะนำสำเร็จ");
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something went wrong.");
    }
  };

  const handleSearch = () => {
    filterPlaces(1, meta.limit, keyword, statusFilter);
  };

  const handleResetSearch = () => {
    setKeyword("");
    setStatusFilter([0, 1]);
    filterPlaces(1, meta.limit, "", [0, 1]);
  };

  useEffect(() => {
    filterPlaces(1, 10);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);

  return (
    <>
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Search and Filter Section */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ค้นหาชื่อสถานที่..."
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <select
                    className="form-control"
                    value={statusFilter.join(",")}
                    onChange={(e) => {
                      const values = e.target.value
                        .split(",")
                        .map((v) => parseInt(v))
                        .filter((v) => !isNaN(v));
                      setStatusFilter(values);
                    }}
                  >
                    <option value="0,1">ทั้งหมด</option>
                    <option value="1">เปิดใช้งาน</option>
                    <option value="0">ปิดใช้งาน</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <button
                    className="btn btn-primary mr-2"
                    onClick={handleSearch}
                  >
                    ค้นหา
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleResetSearch}
                  >
                    รีเซ็ต
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="btn btn-outline-primary"
                  >
                    + เพิ่มสถานที่
                  </button>
                </div>
              </div>

              <div>
                <Datatable
                  data={places}
                  columns={[
                    {
                      title: "รูปภาพ",
                      width: "80px",
                      render: (_1, _2, row: IRecommendPlace) => {
                        return row.picture
                          ? `<img src="${row.picture}" alt="${row.name}" style="width: 60px; height: 60px; object-fit: cover; border-radius: 4px;" />`
                          : `<div style="width: 60px; height: 60px; background-color: #f8f9fa; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #6c757d;">ไม่มีรูป</div>`;
                      },
                    },
                    {
                      title: "ชื่อสถานที่",
                      width: "200px",
                      data: "name",
                    },
                    {
                      title: "คำอธิบาย",
                      width: "300px",
                      render: (_1, _2, row: IRecommendPlace) => {
                        return row.description.length > 100
                          ? `${row.description.substring(0, 100)}...`
                          : row.description;
                      },
                    },
                    {
                      title: "พิกัด",
                      width: "150px",
                      render: (_1, _2, row: IRecommendPlace) => {
                        return `${row.lat.toFixed(6)}, ${row.lng.toFixed(6)}`;
                      },
                    },
                    {
                      title: "สถานะ",
                      width: "100px",
                      data: "status",
                      render: (data: number) => {
                        switch (data) {
                          case 0:
                            return "<span class='badge badge-warning'>ปิดใช้งาน</span>";
                          case 1:
                            return "<span class='badge badge-success'>เปิดใช้งาน</span>";
                          default:
                            return "";
                        }
                      },
                    },
                    {
                      title: "วันที่สร้าง",
                      width: "120px",
                      render: (_1, _2, row: IRecommendPlace) => {
                        return new Date(row.createdAt).toLocaleDateString(
                          "th-TH"
                        );
                      },
                    },
                  ]}
                  actions={[
                    {
                      button: {
                        callback(data: IRecommendPlace) {
                          setShowEditModal({ show: true, place: data });
                        },
                        code: (
                          <button className="btn btn-outline-primary">
                            แก้ไข
                          </button>
                        ),
                      },
                    },
                    {
                      button: {
                        callback(data: IRecommendPlace) {
                          deletePlace(data.id);
                        },
                        code: (
                          <button className="btn btn-outline-danger">ลบ</button>
                        ),
                      },
                      confirm: {
                        title: "ยืนยันการทำรายการ",
                        description: "ยืนยันการลบสถานที่แนะนำ '{name}'",
                        textCancel: "ยกเลิก",
                        classBtnCancel: "btn btn-light",
                        textConfirm: "ยืนยัน",
                        classBtnConfirm: "btn btn-danger",
                      },
                    },
                  ]}
                />

                <Pagination
                  active={meta.page}
                  limit={meta.limit}
                  onPageChange={(page) => {
                    filterPlaces(page, meta.limit);
                  }}
                  pageRangeDisplayed={5}
                  total={meta.totalCount}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddRecommendPlaceModal
        show={showAddModal}
        toggle={() => setShowAddModal(!showAddModal)}
        onSubmit={addNewPlace}
      />

      <EditRecommendPlaceModal
        place={showEditModal.place as IRecommendPlace}
        show={showEditModal.show}
        toggle={() =>
          setShowEditModal((prev) => ({ place: {}, show: !prev.show }))
        }
        onSubmit={updatePlace}
      />
    </>
  );
};

export default SettingRecommendPlace;
